import {
  AxisConfig,
  getAnnotationCenter,
} from 'features/kestrelLite/utils/annotations'
import { Eye, EyeOff, Focus } from 'lucide-react'
import React, { FC, useCallback, useMemo } from 'react'
import { Vector3 } from 'three'
import { KVAnnotation } from 'types'

import {
  AnnotationListHeader,
  AnnotationListItem,
  AnnotationListPanel,
  AnnotationListTitle,
  AnnotationName,
  AnnotationScrollContainer,
  FlyToButton,
  SmallCheckbox,
  ToggleLabel,
  ToggleRow,
  ToggleSection,
  VisibilityToggle,
} from './annotationListStyles'

interface AnnotationListProps {
  annotations: KVAnnotation[]
  axisConfig: AxisConfig
  onFlyTo: (target: Vector3) => void
  showAnnotations: boolean
  onToggleVisibility: () => void
  showLabels: boolean
  onToggleLabels: (show: boolean) => void
  showArea: boolean
  onToggleArea: (show: boolean) => void
}

export const AnnotationList: FC<AnnotationListProps> = ({
  annotations,
  axisConfig,
  onFlyTo,
  showAnnotations,
  onToggleVisibility,
  showLabels,
  onToggleLabels,
  showArea,
  onToggleArea,
}) => {
  const sortedAnnotations = useMemo(() => {
    return [...annotations]
      .filter((a) => a.geometry)
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
  }, [annotations])

  const handleFlyTo = useCallback(
    (annotation: KVAnnotation) => {
      const center = getAnnotationCenter(annotation, axisConfig)
      if (center) {
        onFlyTo(center)
      }
    },
    [axisConfig, onFlyTo]
  )

  if (sortedAnnotations.length === 0) return null

  return (
    <AnnotationListPanel>
      <AnnotationListHeader>
        <AnnotationListTitle>
          Annotations ({sortedAnnotations.length})
        </AnnotationListTitle>
        <VisibilityToggle
          $visible={showAnnotations}
          onClick={onToggleVisibility}
          data-tooltip={
            showAnnotations ? 'Hide annotations' : 'Show annotations'
          }>
          {showAnnotations ? <Eye size={18} /> : <EyeOff size={18} />}
        </VisibilityToggle>
      </AnnotationListHeader>

      <ToggleSection>
        <ToggleRow>
          <ToggleLabel>Show Labels</ToggleLabel>
          <SmallCheckbox
            type='checkbox'
            checked={showLabels}
            onChange={(e) => onToggleLabels(e.target.checked)}
            disabled={!showAnnotations}
          />
        </ToggleRow>
        <ToggleRow>
          <ToggleLabel>Show Area Labels</ToggleLabel>
          <SmallCheckbox
            type='checkbox'
            checked={showArea}
            onChange={(e) => onToggleArea(e.target.checked)}
            disabled={!showAnnotations || !showLabels}
          />
        </ToggleRow>
      </ToggleSection>

      <AnnotationScrollContainer>
        {sortedAnnotations.map((annotation) => {
          const name = annotation.label || annotation.type || annotation.id
          return (
            <AnnotationListItem key={annotation.id}>
              <AnnotationName>{name}</AnnotationName>
              <FlyToButton
                onClick={() => handleFlyTo(annotation)}
                data-tooltip={`Fly to ${name}`}>
                <Focus size={14} />
              </FlyToButton>
            </AnnotationListItem>
          )
        })}
      </AnnotationScrollContainer>
    </AnnotationListPanel>
  )
}
